// src/utils/myBandsCache.js

import { api } from "../services/api";

/**
 * /auth/me/bands -vastauksen kevyt käyttäjäkohtainen muistivälimuisti.
 *
 * Tavoitteet:
 * - Paavalikko ja Dashboard eivät tee samaa HTTP-pyyntöä yhtä aikaa.
 * - Dashboard voi käyttää jo ladattuja profiileja heti.
 * - Tuore data haetaan silti säännöllisesti uudelleen.
 * - Eri käyttäjien data EI koskaan käytä samaa cachea.
 */

const CACHE_TTL_MS = 30_000;

/**
 * Cache tallennetaan käyttäjäkohtaisesti.
 *
 * Map:
 * userKey -> {
 *   response,
 *   cachedAt
 * }
 */
const cacheByUser = new Map();

/**
 * Myös käynnissä oleva API-pyyntö pidetään käyttäjäkohtaisena.
 *
 * Tämä estää esimerkiksi sen, että:
 * - Paavalikko aloittaa /auth/me/bands-haun
 * - Dashboard mounttaa heti perään
 * - Dashboard tekisi saman pyynnön uudestaan
 */
const inFlightByUser = new Map();

/**
 * Muodostaa kirjautuneesta käyttäjästä vakaan cache-avaimen.
 *
 * Ensisijaisesti käytetään id:tä.
 * Sähköposti toimii vain fallbackina vanhemmille user-rakenteille.
 *
 * Jos käyttäjää ei pystytä tunnistamaan varmasti,
 * palautetaan null eikä cachea käytetä lainkaan.
 */
export function getMyBandsUserKey(user) {
  if (!user || typeof user !== "object") {
    return null;
  }

  const id =
    user.id ??
    user.user_id ??
    user._id ??
    user.account?.id ??
    user.account?.user_id ??
    null;

  if (id != null && String(id).trim()) {
    return `user:${String(id).trim()}`;
  }

  const email =
    String(user.email || "")
      .trim()
      .toLowerCase();

  if (email) {
    return `email:${email}`;
  }

  return null;
}

/**
 * Normalisoi backendin mahdolliset eri response-muodot
 * yhteen yhteiseen rakenteeseen.
 */
export function normalizeMyBandsResponse(res) {
  const list =
    Array.isArray(res?.bands)
      ? res.bands
      : Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res?.memberships)
          ? res.memberships
          : Array.isArray(res)
            ? res
            : [];

  const items = list.map((membership) => {
    const profile =
      membership?.band || membership;

    const id =
      profile?.band_id ??
      profile?.id;

    return {
      // Jäsenyystiedot ensin, profiilitiedot päälle.
      ...membership,
      ...profile,

      id,
      band_id: id,

      name:
        profile?.name ??
        profile?.band_name ??
        membership?.name ??
        membership?.band_name ??
        `#${id}`,

      role:
        membership?.role ??
        profile?.role ??
        "member",
    };
  });

  const meta =
    res?.meta || {};

  const maxBands =
    Number(
      meta.max_bands ?? 1
    );

  const ownerCount =
    Number(
      meta.bands_count ??
        items.filter(
          (item) =>
            String(
              item?.role || ""
            ).toLowerCase() ===
            "owner"
        ).length
    );

  return {
    items,
    meta,

    limits: {
      max_bands: maxBands,
      bands_count: ownerCount,
      can_add_band:
        ownerCount < maxBands,
    },
  };
}

/**
 * Palauttaa käyttäjän cache-entryn.
 */
function getCacheEntry(cacheKey) {
  if (!cacheKey) {
    return null;
  }

  return (
    cacheByUser.get(cacheKey) ??
    null
  );
}

/**
 * Palauttaa muistissa olevan käyttäjän /auth/me/bands-vastauksen.
 *
 * allowStale=true:
 * Dashboard saa käyttää myös yli 30 sekuntia vanhaa versiota
 * heti ensimmäiseen renderiin. getMyBands() voi tämän jälkeen
 * hakea tarvittaessa tuoreen version.
 *
 * Jos cacheKey puuttuu, cachea EI käytetä.
 */
export function peekMyBands({
  allowStale = false,
  cacheKey = null,
} = {}) {
  if (!cacheKey) {
    return null;
  }

  const entry =
    getCacheEntry(cacheKey);

  if (!entry?.response) {
    return null;
  }

  if (allowStale) {
    return entry.response;
  }

  const age =
    Date.now() -
    Number(entry.cachedAt || 0);

  if (age > CACHE_TTL_MS) {
    return null;
  }

  return entry.response;
}

/**
 * Hakee kirjautuneen käyttäjän profiilit.
 *
 * Jos saman käyttäjän haku on jo käynnissä,
 * palautetaan sama Promise uuden HTTP-pyynnön sijasta.
 *
 * Jos cacheKey puuttuu:
 * - cachea ei lueta
 * - vastausta ei tallenneta cacheen
 *
 * Näin tuntemattoman käyttäjän data ei voi vahingossa
 * sekoittua toisen käyttäjän cacheen.
 */
export async function getMyBands({
  force = false,
  cacheKey = null,
} = {}) {
  /**
   * Ilman käyttäjäavainta mennään turvallisesti
   * suoraan backendille ilman cachea.
   */
  if (!cacheKey) {
    return api.auth.meBands();
  }

  if (!force) {
    const cached =
      peekMyBands({
        cacheKey,
      });

    if (cached) {
      return cached;
    }
  }

  /**
   * Jos saman käyttäjän pyyntö on jo käynnissä,
   * käytetään sitä.
   */
  const existingRequest =
    inFlightByUser.get(cacheKey);

  if (existingRequest) {
    return existingRequest;
  }

  const request =
    Promise.resolve(
      api.auth.meBands()
    )
      .then((res) => {
        cacheByUser.set(
          cacheKey,
          {
            response: res,
            cachedAt: Date.now(),
          }
        );

        return res;
      })
      .finally(() => {
        /**
         * Poistetaan vain juuri tämä Promise.
         *
         * Tämä suojaa tilannetta, jossa mapissa olisi
         * jostain syystä jo uudempi request.
         */
        if (
          inFlightByUser.get(
            cacheKey
          ) === request
        ) {
          inFlightByUser.delete(
            cacheKey
          );
        }
      });

  inFlightByUser.set(
    cacheKey,
    request
  );

  return request;
}

/**
 * Tyhjentää yhden käyttäjän välimuistin.
 *
 * Käytä esimerkiksi kun:
 * - profiili luodaan
 * - profiili poistetaan
 * - jäsenyys muuttuu
 * - halutaan varmasti seuraavalla kerralla uusi lista
 */
export function invalidateMyBandsCache(
  cacheKey
) {
  if (!cacheKey) {
    return;
  }

  cacheByUser.delete(cacheKey);
}

/**
 * Tyhjentää kaikki käyttäjäkohtaiset cachet.
 *
 * Tätä voidaan käyttää esimerkiksi logout-flow'ssa,
 * jos halutaan vapauttaa kaikki muistissa oleva data.
 *
 * Käyttäjäkohtaisen avaimen ansiosta tämä ei ole
 * turvallisuuden kannalta pakollinen normaalissa käytössä.
 */
export function clearMyBandsCache() {
  cacheByUser.clear();
  inFlightByUser.clear();
}